import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";

const SECTIONS = [
  {
    title: "1. What we collect",
    body: "Account details (name, email, business profile), the business data you enter (leads, contacts, appointments, voice agent settings, marketing content and drafts), billing records handled by our payment provider, and technical logs needed to run and secure the service (for example security audit events with hashed IP addresses — we do not store raw IPs in those logs).",
  },
  {
    title: "2. How we use it",
    body: "To operate your account and the features you use, to secure the platform, to process billing, and to contact you about your account. We do not sell your data. Each business account is isolated — other customers cannot access your records.",
  },
  {
    title: "3. Data you enter about other people",
    body: "When you add leads or customers to the CRM, you are the controller of that data and must have a lawful basis to hold and use it, including any consent needed for calls or marketing. We process it on your instructions as a processor.",
  },
  {
    title: "4. Retention and deletion",
    body: "We keep your account data while your account is active. You can delete leads, appointments and content from your dashboard at any time. To delete your account and remaining data, email support@savykernoai.com from your account address. Security and billing records may be retained for a limited period where required for fraud prevention, tax or legal reasons.",
  },
  {
    title: "5. Third-party processors",
    body: "We use third-party providers to deliver the service — including cloud hosting, database hosting, a payment provider, AI model providers and, where you configure them, email, telephony and social platforms. Only the data needed for each function is shared with the relevant provider.",
  },
  {
    title: "6. Security practices",
    body: "Passwords are stored as strong one-way hashes (bcrypt), sessions use signed tokens, sensitive configuration is held server-side, and access between customer accounts is enforced on every request. No method is perfectly secure; if we become aware of a breach affecting your data we will inform you.",
  },
  {
    title: "7. Your rights",
    body: "Depending on your location (including the UK and EEA), you may have rights to access, correct, export, restrict or delete your personal data, and to object to certain processing. Email support@savykernoai.com to exercise them — we will respond within the legally required period.",
  },
  {
    title: "8. Cookies",
    body: "We use essential storage for sign-in sessions and preferences. We do not use advertising trackers on the dashboard.",
  },
  {
    title: "9. Contact",
    body: "Privacy questions and requests: support@savykernoai.com. This policy is pending final legal review before paid launch.",
  },
  {
    title: "10. Lawful bases (UK GDPR)",
    body: "We process account and service data to perform our contract with you; security and audit logging under our legitimate interest in keeping the platform safe; and any optional communications with your consent, which you can withdraw at any time. For the customer data you place in the CRM, you are the controller and we act on your instructions — see the Data Processing Agreement.",
  },
  {
    title: "11. Marketing rules (PECR)",
    body: "Electronic marketing from the platform follows the Privacy and Electronic Communications Regulations: email marketing requires the recipient's consent or a valid soft opt-in, outbound automated calling requires prior specific consent, and suppression requests must be honoured. The CRM records consent explicitly — an import never grants it by default.",
  },
  {
    title: "12. AI processing and recordings",
    body: "Voice conversations are transcribed and answered using OpenAI's API under our enterprise configuration; API inputs are not used to train provider models. Call audio recording is off by default — see the AI Calling & Recording Policy for what is stored and what callers are told.",
  },
  {
    title: "13. International transfers",
    body: "Some providers process data outside the UK. Where that happens we rely on their UK GDPR transfer safeguards (for example the UK Addendum to EU standard contractual clauses).",
  },
  {
    title: "14. Complaints",
    body: "You can complain to us at support@savykernoai.com. If you are in the UK you also have the right to complain to the Information Commissioner's Office (ico.org.uk).",
  },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <header className="border-b border-neutral-200">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4">
          <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-brand">
            <BrandLogo />
            <span className="text-base font-bold tracking-tight">Savy Kerno</span>
          </Link>
          <Link to="/login" className="text-sm font-medium text-neutral-600 hover:text-[#111111]">
            Sign In
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-5 py-14">
        <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="mt-2 text-xs text-neutral-400">Version 2026-10 · Last updated: October 2026</p>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-neutral-600">
          This policy explains what Savy Kerno collects, why, and the choices you have.
          It is written to be read, not to hide behind.
        </p>
        <div className="mt-10 space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.title}>
              <h2 className="text-base font-bold text-[#111111]">{s.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-neutral-600">{s.body}</p>
            </section>
          ))}
        </div>
        <p className="mt-12 text-sm text-neutral-500">
          See also our <Link to="/terms" className="font-semibold underline underline-offset-2">Terms of Service</Link> and{" "}
          <Link to="/support" className="font-semibold underline underline-offset-2">Support page</Link>.
        </p>
      </main>
    </div>
  );
}
